import React from "react";
import {
  SimpleGrid,
  Card,
  Text,
  VStack,
  HStack,
  Skeleton,
} from "@chakra-ui/react";
import {
  FaBell,
  FaEye,
  FaTriangleExclamation,
  FaListCheck,
} from "react-icons/fa6";
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy";

interface AlertHeadlinesProps {
  headlines?: {
    total_active: number;
    by_status: {
      new: number;
      acknowledged: number;
      attended: number;
      completed: number;
    };
    by_severity: {
      critical: number;
      warning: number;
      info: number;
    };
  };
  loading?: boolean;
}

interface HeadlineCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<any>;
  color: string;
  loading?: boolean;
}

function HeadlineCard({
  title,
  value,
  icon: Icon,
  color,
  loading,
}: HeadlineCardProps) {
  return (
    <Card.Root>
      <Card.Body>
        <HStack justify="space-between">
          <VStack align="start" gap={1}>
            <Text fontSize="sm" color="fg.muted">
              {title}
            </Text>
            {loading ? (
              <Skeleton height="32px" width="60px" />
            ) : (
              <Text fontSize="2xl" fontWeight="bold" color={color}>
                {value.toLocaleString()}
              </Text>
            )}
          </VStack>
          <Icon size={24} color={color} />
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

export function AlertHeadlines({ headlines, loading }: AlertHeadlinesProps) {
  const copy = useCopyValues({
    "headlines.total_active": "Total Active",
    "headlines.new_alerts": "New Alerts",
    "headlines.critical_alerts": "Critical Alerts",
    "headlines.acknowledged_alerts": "Acknowledged Alerts",
    "headlines.attended_alerts": "Attended Alerts",
  });

  const headlineCards = [
    {
      title: copy["headlines.total_active"],
      value: headlines?.total_active || 0,
      icon: FaListCheck,
      color: "blue.600",
    },
    {
      title: copy["headlines.new_alerts"],
      value: headlines?.by_status?.new || 0,
      icon: FaBell,
      color: "red.600",
    },
    {
      title: copy["headlines.critical_alerts"],
      value: headlines?.by_severity?.critical || 0,
      icon: FaTriangleExclamation,
      color: "red.600",
    },
    {
      title: copy["headlines.acknowledged_alerts"],
      value: headlines?.by_status?.acknowledged || 0,
      icon: FaEye,
      color: "blue.600",
    },
    {
      title: copy["headlines.attended_alerts"],
      value: headlines?.by_status?.attended || 0,
      icon: FaEye,
      color: "purple.600",
    },
  ];

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4} mb={6}>
      {headlineCards.map((card, index) => (
        <HeadlineCard
          key={index}
          title={card.title}
          value={card.value}
          icon={card.icon}
          color={card.color}
          loading={loading}
        />
      ))}
    </SimpleGrid>
  );
}

export default AlertHeadlines;
